import type { tLayerSettings } from '@/atoms';
import * as THREE from 'three';

const DEG2RAD = Math.PI / 180;

/**
 * Pulls the per-layer transform out of a settings record. Returns
 * tuples ready to drop into JSX (`<group position={offset} rotation={rotation}>`)
 * and the THREE primitives for renderers that compose with the
 * scene-graph imperatively.
 *
 * Defaults to identity (zero offset, zero rotation) when the layer
 * settings haven't been hydrated yet — keeps every renderer's "no
 * settings yet" path rendering at the original world location.
 *
 * Rotation is stored in degrees in `LayerSettings` so the dialog can
 * show whole-degree values without floating-point drift; we convert
 * to radians here and apply them in Euler XYZ order. Mirrors r3f's
 * `<group rotation={[x, y, z]}>` semantics.
 */
export function getLayerTransform(settings: tLayerSettings | null | undefined): {
  offset: [number, number, number];
  rotation: [number, number, number];
  hasOffset: boolean;
  hasRotation: boolean;
  isIdentity: boolean;
  offsetVec: THREE.Vector3;
  euler: THREE.Euler;
  quaternion: THREE.Quaternion;
} {
  const ox = settings?.offsetX ?? 0;
  const oy = settings?.offsetY ?? 0;
  const oz = settings?.offsetZ ?? 0;
  const rx = (settings?.rotationX ?? 0) * DEG2RAD;
  const ry = (settings?.rotationY ?? 0) * DEG2RAD;
  const rz = (settings?.rotationZ ?? 0) * DEG2RAD;

  const offsetVec = new THREE.Vector3(ox, oy, oz);
  const euler = new THREE.Euler(rx, ry, rz, 'XYZ');
  const quaternion = new THREE.Quaternion().setFromEuler(euler);

  const hasOffset = ox !== 0 || oy !== 0 || oz !== 0;
  const hasRotation = rx !== 0 || ry !== 0 || rz !== 0;

  return {
    offset: [ox, oy, oz],
    rotation: [rx, ry, rz],
    hasOffset,
    hasRotation,
    isIdentity: !hasOffset && !hasRotation,
    offsetVec,
    euler,
    quaternion,
  };
}
